import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Avatar,
  Divider,
  Badge,
  Stack,
  Snackbar,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  DoneAll as DoneAllIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import QuizFunnelService, { QuizLeadSummary, QuizFunnelStats } from '../services/quizFunnelService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Mapeamento de etapas do quiz
const ETAPAS_MAP: Record<string, { label: string; color: string }> = {
  '1.1': { label: 'Nome', color: '#2196F3' },
  '1.2': { label: 'WhatsApp', color: '#4CAF50' },
  '1.3': { label: 'Email', color: '#FF9800' },
  '2': { label: 'Mercado', color: '#9C27B0' },
  '3': { label: 'Meta', color: '#F44336' },
  '4': { label: 'Gargalo', color: '#00BCD4' },
  '5': { label: 'Ferramentas', color: '#795548' },
  '6': { label: 'Frustração', color: '#607D8B' },
  '7': { label: 'Investimento', color: '#3F51B5' },
  '8': { label: 'Leads/dia', color: '#009688' },
  '9': { label: 'Disponibilidade', color: '#CDDC39' },
  '10': { label: 'Decisor', color: '#FF5722' },
};

const QuizFunnelTracker: React.FC = () => {
  const [leads, setLeads] = useState<QuizLeadSummary[]>([]);
  const [stats, setStats] = useState<QuizFunnelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<QuizLeadSummary | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [whatsappLead, setWhatsappLead] = useState<QuizLeadSummary | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deletingLeadId, setDeletingLeadId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    etapa: '',
    isCompleted: '',
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
  });

  // Carregar dados
  const loadData = async () => {
    setLoading(true);
    try {
      const [leadsResponse, statsResponse] = await Promise.all([
        QuizFunnelService.listLeads({
          limit: pagination.limit,
          offset: pagination.offset,
          search: filters.search || undefined,
          etapa: filters.etapa || undefined,
          isCompleted: filters.isCompleted ? filters.isCompleted === 'true' : undefined,
        }),
        QuizFunnelService.getStats(),
      ]);

      if (leadsResponse.success) {
        setLeads(leadsResponse.data || []);
        setPagination(prev => ({ ...prev, total: leadsResponse.total || 0 }));
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pagination.offset, filters]);

  const handleViewLead = (lead: QuizLeadSummary) => {
    setSelectedLead(lead);
    setDialogOpen(true);
  };

  const handleDeleteLead = async (leadUUID: string, leadName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o lead "${leadName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setDeletingLeadId(leadUUID);

    try {
      const result = await QuizFunnelService.deleteLead(leadUUID);

      if (result.success) {
        setSuccessMessage('Lead excluído com sucesso!');
        // Recarregar dados
        loadData();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(result.error || 'Erro ao excluir lead');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error: any) {
      console.error('Erro ao excluir lead:', error);
      const errorMessage = error.response?.data?.error || error.message || error.toString() || 'Erro ao excluir lead. Tente novamente.';
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleOpenWhatsappDialog = (lead: QuizLeadSummary) => {
    const phone = lead.whatsapp || lead.contact_phone;
    if (!phone) {
      setErrorMessage('Este lead não possui número de telefone cadastrado.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    setWhatsappLead(lead);
    setWhatsappMessage('');
    setWhatsappDialogOpen(true);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleCloseWhatsappDialog = () => {
    setWhatsappDialogOpen(false);
    setWhatsappMessage('');
    setWhatsappLead(null);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleSendWhatsappMessage = async () => {
    if (!whatsappLead) return;
    
    const phone = whatsappLead.whatsapp || whatsappLead.contact_phone;
    if (!phone) {
      setErrorMessage('Este lead não possui número de telefone cadastrado.');
      return;
    }

    if (!whatsappMessage.trim()) {
      setErrorMessage('Por favor, insira uma mensagem.');
      return;
    }

    setSendingMessage(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await fetch('https://n8n-webhook.i2pq5v.easypanel.host/webhook/4408fafb-cf8e-469f-9d52-62c96c2642bc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phone,
          message: whatsappMessage.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro ao enviar mensagem: ${response.status}`);
      }

      const data = await response.json();
      
      // Marcar mensagem como enviada no backend
      try {
        await QuizFunnelService.markMessageSent(whatsappLead.lead_uuid, whatsappMessage.trim());
      } catch (markError) {
        console.error('Erro ao marcar mensagem como enviada:', markError);
        // Não bloquear o fluxo se falhar, apenas logar
      }
      
      setSuccessMessage('Mensagem enviada com sucesso!');
      // Recarregar dados para atualizar o indicador
      loadData();
      setTimeout(() => {
        handleCloseWhatsappDialog();
      }, 1500);
    } catch (error: any) {
      console.error('Erro ao enviar mensagem WhatsApp:', error);
      setErrorMessage(error.message || 'Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatResposta = (resposta: any): string => {
    if (typeof resposta === 'string') return resposta;
    if (typeof resposta === 'number') return resposta.toString();
    if (Array.isArray(resposta)) return resposta.join(', ');
    if (typeof resposta === 'object' && resposta !== null) {
      if (resposta.selected) {
        const parts = Array.isArray(resposta.selected) ? resposta.selected : [resposta.selected];
        if (resposta.other) parts.push(`Outro: ${resposta.other}`);
        if (resposta.decisionMakerName) parts.push(`Decisor: ${resposta.decisionMakerName}`);
        if (resposta.decisionMakerContact) parts.push(`Contato: ${resposta.decisionMakerContact}`);
        return parts.join(', ');
      }
      return JSON.stringify(resposta);
    }
    return 'N/A';
  };

  const getEtapaProgress = (lead: QuizLeadSummary): number => {
    if (lead.is_completed) return 100;
    if (!lead.current_etapa) return 0;
    
    const etapaIndex = Object.keys(ETAPAS_MAP).indexOf(lead.current_etapa);
    if (etapaIndex === -1) return 0;
    
    return Math.round(((etapaIndex + 1) / Object.keys(ETAPAS_MAP).length) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box className="glass-blue-card rounded-xl mb-6 p-4">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}>
          {/* Título - Esquerda */}
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              color: '#000000',
              fontSize: { xs: '1.5rem', md: '2.125rem' },
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              Quiz Funnel Tracker
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              Rastreamento de leads em funis de quiz interativos (estilo Inlead)
            </Typography>
          </Box>

          {/* Botão Refresh - Direita */}
          <Tooltip title="Atualizar">
            <IconButton 
              onClick={loadData} 
              disabled={loading}
              sx={{
                color: '#3b82f6',
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filtros - Linha única */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Busca - Esquerda */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nome, email, telefone ou UUID..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  '& fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.8)',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#000000',
                },
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} />,
              }}
            />
          </Box>

          {/* Filtros - Centro */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#64748b' }}>Etapa</InputLabel>
              <Select
                value={filters.etapa}
                label="Etapa"
                onChange={(e) => setFilters({ ...filters, etapa: e.target.value })}
                sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  color: '#000000',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.8)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#64748b',
                  },
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(ETAPAS_MAP).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#64748b' }}>Status</InputLabel>
              <Select
                value={filters.isCompleted}
                label="Status"
                onChange={(e) => setFilters({ ...filters, isCompleted: e.target.value })}
                sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  color: '#000000',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.8)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#64748b',
                  },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Completados</MenuItem>
                <MenuItem value="false">Em andamento</MenuItem>
                <MenuItem value="abandoned">Abandonados</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Botão Limpar - Direita */}
          <Button
            variant="outlined"
            onClick={() => setFilters({ search: '', etapa: '', isCompleted: '' })}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#000000',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.5)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Limpar
          </Button>
        </Box>
      </Box>

      {/* Estatísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={stats.averageAbandonmentTime !== null ? 2 : 3}>
            <Box className="glass-blue-card rounded-xl p-4 hover:shadow-violet-500/10 transition-all duration-300 hover:scale-105">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 24 }} />
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Total de Leads
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {stats.totalLeads}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={stats.averageAbandonmentTime !== null ? 2 : 3}>
            <Box className="glass-blue-card rounded-xl p-4 hover:shadow-green-500/10 transition-all duration-300 hover:scale-105">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, color: '#10b981', fontSize: 24 }} />
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Completados
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {stats.completedLeads}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                {stats.totalLeads > 0
                  ? `${Math.round((stats.completedLeads / stats.totalLeads) * 100)}%`
                  : '0%'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={stats.averageAbandonmentTime !== null ? 2 : 3}>
            <Box className="glass-blue-card rounded-xl p-4 hover:shadow-red-500/10 transition-all duration-300 hover:scale-105">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CancelIcon sx={{ mr: 1, color: '#ef4444', fontSize: 24 }} />
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Abandonados
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {stats.abandonedLeads}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                {stats.totalLeads > 0
                  ? `${Math.round((stats.abandonedLeads / stats.totalLeads) * 100)}%`
                  : '0%'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="glass-blue-card rounded-xl p-4 hover:shadow-orange-500/10 transition-all duration-300 hover:scale-105">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: '#f59e0b', fontSize: 24 }} />
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Tempo Médio Completo
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                {stats.averageCompletionTime
                  ? `${Math.round(stats.averageCompletionTime)} min`
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          {stats.averageAbandonmentTime !== null && (
            <Grid item xs={12} sm={6} md={3}>
              <Box className="glass-blue-card rounded-xl p-4 hover:shadow-red-500/10 transition-all duration-300 hover:scale-105">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ mr: 1, color: '#ef4444', fontSize: 24 }} />
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Tempo Médio Abandono
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {stats.averageAbandonmentTime
                    ? `${Math.round(stats.averageAbandonmentTime)} min`
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}


      {/* Tabela de Leads */}
      <Box className="glass-blue-card rounded-xl p-4">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#3b82f6' }} />
          </Box>
        ) : leads.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: '#000000',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}
          >
            Nenhum lead encontrado.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Lead
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Contato
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Etapa Atual
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Progresso
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Respostas
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Última Atividade
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Tempo Total
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      color: '#000000',
                      fontWeight: 'bold',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow 
                      key={lead.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        },
                        '& td': {
                          color: '#000000',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ color: '#000000' }}>
                                {lead.nome || lead.contact_name || 'Sem nome'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                {lead.lead_uuid.substring(0, 8)}...
                              </Typography>
                            </Box>
                            {lead.metadata?.last_whatsapp_sent_at && (
                              <Tooltip 
                                title={`Mensagem enviada em ${format(new Date(lead.metadata.last_whatsapp_sent_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}`}
                                arrow
                              >
                                <Badge
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                  }}
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      backgroundColor: '#25D366',
                                      color: '#000000',
                                      width: 20,
                                      height: 20,
                                      minWidth: 20,
                                      border: '2px solid white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    },
                                  }}
                                >
                                  <DoneAllIcon 
                                    sx={{ 
                                      fontSize: 18, 
                                      color: '#25D366',
                                    }} 
                                  />
                                </Badge>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {lead.whatsapp || lead.contact_phone ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                <Typography variant="caption" sx={{ color: '#000000' }}>
                                  {lead.whatsapp || lead.contact_phone}
                                </Typography>
                              </Box>
                            ) : null}
                            {lead.email || lead.contact_email ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                                <Typography variant="caption" sx={{ color: '#000000' }}>
                                  {lead.email || lead.contact_email}
                                </Typography>
                              </Box>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {lead.current_etapa ? (
                            <Chip
                              label={ETAPAS_MAP[lead.current_etapa]?.label || lead.current_etapa}
                              size="small"
                              sx={{
                                bgcolor: ETAPAS_MAP[lead.current_etapa]?.color || 'grey.300',
                                color: '#000000',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              Não iniciado
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={getEtapaProgress(lead)}
                              sx={{ 
                                height: 8, 
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#3b82f6',
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              {getEtapaProgress(lead)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.total_responses || 0}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(59, 130, 246, 0.2)',
                              color: '#c4b5fd',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                            }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#000000' }}>
                            {lead.last_response_at || lead.last_activity_at
                              ? format(
                                  new Date(lead.last_response_at || lead.last_activity_at),
                                  'dd/MM/yyyy HH:mm',
                                  { locale: ptBR }
                                )
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {lead.tempo_total_funil !== null && lead.tempo_total_funil > 0 ? (
                            <Tooltip title={`${lead.tempo_total_funil} segundos`}>
                              <Typography variant="body2" sx={{ color: '#000000' }}>
                                {Math.floor(lead.tempo_total_funil / 60)}m {lead.tempo_total_funil % 60}s
                              </Typography>
                            </Tooltip>
                          ) : lead.tempo_ultima_resposta !== null && lead.tempo_ultima_resposta > 0 ? (
                            <Tooltip title={`Tempo até última resposta: ${lead.tempo_ultima_resposta}s`}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                ~{Math.floor(lead.tempo_ultima_resposta / 60)}m {lead.tempo_ultima_resposta % 60}s
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.is_completed ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ color: '#10b981' }} />}
                              label="Completo"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                color: '#6ee7b7',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                              }}
                            />
                          ) : lead.abandoned_at ? (
                            <Chip
                              icon={<CancelIcon sx={{ color: '#ef4444' }} />}
                              label={`Abandonado${lead.metadata?.motivo_abandono ? ` - ${lead.metadata.motivo_abandono.replace('_', ' ')}` : ''}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                color: '#fca5a5',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<TrendingUpIcon sx={{ color: '#f59e0b' }} />}
                              label="Em andamento"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                color: '#fcd34d',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => handleViewLead(lead)}
                                sx={{
                                  color: '#3b82f6',
                                  '&:hover': {
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  },
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {(lead.whatsapp || lead.contact_phone) && (
                              <Tooltip title="Enviar mensagem WhatsApp">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenWhatsappDialog(lead)}
                                  sx={{
                                    color: '#25D366',
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    },
                                  }}
                                >
                                  <WhatsAppIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Excluir lead">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteLead(lead.lead_uuid, lead.nome || lead.contact_name || 'Lead sem nome')}
                                disabled={deletingLeadId === lead.lead_uuid}
                                sx={{
                                  color: '#ef4444',
                                  '&:hover': {
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                  },
                                  '&.Mui-disabled': {
                                    color: 'rgba(239, 68, 68, 0.3)',
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Mostrando {pagination.offset + 1} -{' '}
                  {Math.min(pagination.offset + pagination.limit, pagination.total)} de{' '}
                  {pagination.total} leads
                </Typography>
                <Box>
                  <Button
                    disabled={pagination.offset === 0}
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        offset: Math.max(0, pagination.offset - pagination.limit),
                      })
                    }
                    sx={{
                      color: '#000000',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    variant="outlined"
                  >
                    Anterior
                  </Button>
                  <Button
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        offset: pagination.offset + pagination.limit,
                      })
                    }
                    sx={{
                      color: '#000000',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      ml: 1,
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    variant="outlined"
                  >
                    Próximo
                  </Button>
                </Box>
              </Box>
            </>
          )}
      </Box>

      {/* Dialog de Detalhes */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            <Typography variant="h6">
              Detalhes do Lead: {selectedLead?.nome || selectedLead?.contact_name || 'Sem nome'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLead && (
            <Stack spacing={3}>
              {/* Informações básicas */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Informações de Contato
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PhoneIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>WhatsApp/Telefone:</strong>{' '}
                        {selectedLead.whatsapp || selectedLead.contact_phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>Email:</strong> {selectedLead.email || selectedLead.contact_email || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>UUID:</strong> {selectedLead.lead_uuid}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Status e progresso */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Status e Progresso
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Etapa Atual:</strong>{' '}
                      {selectedLead.current_etapa
                        ? ETAPAS_MAP[selectedLead.current_etapa]?.label || selectedLead.current_etapa
                        : 'Não iniciado'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total de Respostas:</strong> {selectedLead.total_responses}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong>{' '}
                      {selectedLead.is_completed ? (
                        <Chip label="Completo" size="small" color="success" />
                      ) : selectedLead.abandoned_at ? (
                        <Chip 
                          label={`Abandonado${selectedLead.metadata?.motivo_abandono ? ` - ${selectedLead.metadata.motivo_abandono.replace('_', ' ')}` : ''}`} 
                          size="small" 
                          color="error" 
                        />
                      ) : (
                        <Chip label="Em andamento" size="small" color="warning" />
                      )}
                    </Typography>
                    {selectedLead.metadata?.progresso && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Progresso:</strong> {selectedLead.metadata.progresso.respondidas} / {selectedLead.metadata.progresso.total} ({selectedLead.metadata.progresso.percentual}%)
                      </Typography>
                    )}
                    {(selectedLead.tempo_total_funil !== null && selectedLead.tempo_total_funil > 0) && (
                      <Typography variant="body2">
                        <strong>Tempo Total no Quiz:</strong> {Math.floor(selectedLead.tempo_total_funil / 60)}m {selectedLead.tempo_total_funil % 60}s ({selectedLead.tempo_total_funil} segundos)
                      </Typography>
                    )}
                    {selectedLead.tempo_primeira_resposta !== null && selectedLead.tempo_primeira_resposta > 0 && (
                      <Typography variant="body2">
                        <strong>Tempo até Primeira Resposta:</strong> {selectedLead.tempo_primeira_resposta}s
                      </Typography>
                    )}
                    {selectedLead.tempo_ultima_resposta !== null && selectedLead.tempo_ultima_resposta > 0 && (
                      <Typography variant="body2">
                        <strong>Tempo até Última Resposta:</strong> {selectedLead.tempo_ultima_resposta}s
                      </Typography>
                    )}
                    {selectedLead.metadata?.tempo_quiz_segundos && !selectedLead.tempo_total_funil && (
                      <Typography variant="body2">
                        <strong>Tempo no Quiz (metadata):</strong> {Math.round(selectedLead.metadata.tempo_quiz_segundos / 60)} min ({selectedLead.metadata.tempo_quiz_segundos}s)
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Primeira Resposta:</strong>{' '}
                      {selectedLead.first_response_at
                        ? format(new Date(selectedLead.first_response_at), 'dd/MM/yyyy HH:mm', {
                            locale: ptBR,
                          })
                        : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Última Resposta:</strong>{' '}
                      {selectedLead.last_response_at
                        ? format(new Date(selectedLead.last_response_at), 'dd/MM/yyyy HH:mm', {
                            locale: ptBR,
                          })
                        : 'N/A'}
                    </Typography>
                    {selectedLead.completed_at && (
                      <Typography variant="body2">
                        <strong>Completado em:</strong>{' '}
                        {format(new Date(selectedLead.completed_at), 'dd/MM/yyyy HH:mm', {
                          locale: ptBR,
                        })}
                      </Typography>
                    )}
                    {selectedLead.abandoned_at && (
                      <Typography variant="body2">
                        <strong>Abandonado em:</strong>{' '}
                        {format(new Date(selectedLead.abandoned_at), 'dd/MM/yyyy HH:mm', {
                          locale: ptBR,
                        })}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Respostas */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Respostas do Quiz
                </Typography>
                    {selectedLead.respostas && selectedLead.respostas.length > 0 ? (
                      <Stack spacing={2}>
                        {selectedLead.respostas.map((resposta, index) => {
                          const tempoAnterior = index > 0 && selectedLead.respostas[index - 1]?.tempo_no_funil;
                          const tempoAtual = resposta.tempo_no_funil;
                          const tempoNaPergunta = tempoAtual && tempoAnterior ? tempoAtual - tempoAnterior : tempoAtual;
                          
                          return (
                            <Card key={index} variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Chip
                                    label={ETAPAS_MAP[resposta.etapa]?.label || resposta.etapa}
                                    size="small"
                                    sx={{
                                      bgcolor: ETAPAS_MAP[resposta.etapa]?.color || 'grey.300',
                                      color: '#000000',
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {tempoAtual && (
                                      <Tooltip title={`Tempo total: ${tempoAtual}s${tempoNaPergunta ? ` | Nesta pergunta: ${tempoNaPergunta}s` : ''}`}>
                                        <Chip
                                          label={`${Math.floor(tempoAtual / 60)}m ${tempoAtual % 60}s`}
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      {format(new Date(resposta.timestamp), 'dd/MM/yyyy HH:mm', {
                                        locale: ptBR,
                                      })}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography variant="body2" fontWeight="medium" gutterBottom>
                                  {resposta.pergunta}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Resposta:</strong> {formatResposta(resposta.resposta)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Chip
                                    label={resposta.tipo_pergunta}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {tempoNaPergunta && tempoNaPergunta > 0 && (
                                    <Tooltip title={`Tempo gasto especificamente nesta pergunta`}>
                                      <Chip
                                        label={`Tempo: ${tempoNaPergunta}s`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                ) : (
                  <Alert severity="info">Nenhuma resposta registrada ainda.</Alert>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Envio de Mensagem WhatsApp */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={handleCloseWhatsappDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            <Typography variant="h6">
              Enviar Mensagem WhatsApp
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {whatsappLead && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Lead
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {whatsappLead.nome || whatsappLead.contact_name || 'Sem nome'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Telefone
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {whatsappLead.whatsapp || whatsappLead.contact_phone}
                </Typography>
              </Box>
              <TextField
                label="Mensagem"
                multiline
                rows={6}
                fullWidth
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                placeholder="Digite sua mensagem aqui..."
                variant="outlined"
                disabled={sendingMessage}
              />
              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success">
                  {successMessage}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWhatsappDialog} disabled={sendingMessage}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendWhatsappMessage}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={sendingMessage || !whatsappMessage.trim()}
            sx={{
              backgroundColor: '#25D366',
              '&:hover': {
                backgroundColor: '#128C7E',
              },
            }}
          >
            {sendingMessage ? 'Enviando...' : 'Enviar Mensagem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens de erro/sucesso globais */}
      <Snackbar
        open={!!errorMessage && !whatsappDialogOpen}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizFunnelTracker;

